const KM_PER_DEGREE_LAT = 110.574;
const KM_PER_DEGREE_LNG = 111.320;

// Spacing we'd like between candidate marker positions, in kilometres.
const DEFAULT_CELL_SIDE_KM = 0.05;

// Ceiling on how many points turf.pointGrid() may emit for one paddock.
// pointGrid() materialises the whole grid over the polygon's *bounding box*
// before anything is filtered out, and the count grows with the box's area:
// (widthKm / cellSide) * (heightKm / cellSide). A paddock drawn while zoomed
// right out - production has several the size of an Australian state - asks for
// billions of points at the default 50m spacing and kills the browser tab.
const MAX_GRID_POINTS = 50000;

const boundsSizeInKm = (bounds) => {
  const north = bounds.getNorth();
  const south = bounds.getSouth();
  const midLat = (north + south) / 2;

  return {
    heightKm: Math.abs(north - south) * KM_PER_DEGREE_LAT,
    widthKm:
      Math.abs(bounds.getEast() - bounds.getWest()) *
      KM_PER_DEGREE_LNG *
      Math.abs(Math.cos((midLat * Math.PI) / 180)),
  };
};

// Cell spacing to hand turf.pointGrid() for a paddock, widened just enough that
// an oversized bounding box can't exhaust the tab's memory. Ordinary paddocks
// (up to roughly 11km x 11km) keep the default 50m spacing untouched.
const cellSideForBounds = (
  bounds,
  desiredCellSideKm = DEFAULT_CELL_SIDE_KM,
  maxPoints = MAX_GRID_POINTS
) => {
  const { widthKm, heightKm } = boundsSizeInKm(bounds);

  if (!widthKm || !heightKm || !isFinite(widthKm) || !isFinite(heightKm)) {
    return desiredCellSideKm;
  }

  const smallestSafeCellSide = Math.sqrt((widthKm * heightKm) / maxPoints);

  return Math.max(desiredCellSideKm, smallestSafeCellSide);
};

// Two placements count as the same spot when they are within this many metres.
// Candidate positions sit on a grid at least DEFAULT_CELL_SIDE_KM apart, so this
// only ever collapses a candidate onto a mob already standing on it.
const SAME_POSITION_TOLERANCE_M = 10;

const metresBetween = (a, b) => {
  const aLat = Number(a.lat);
  const latKm = (aLat - Number(b.lat)) * KM_PER_DEGREE_LAT;
  const lngKm =
    (Number(a.lng) - Number(b.lng)) *
    KM_PER_DEGREE_LNG *
    Math.abs(Math.cos((aLat * Math.PI) / 180));

  return Math.sqrt(latKm * latKm + lngKm * lngKm) * 1000;
};

// Is `point` already occupied by one of `taken`?
// Coordinates reach us straight off the API, where the decimal lat/lng columns
// arrive as strings ("144.97547820"), while candidates are numbers formatted by
// toFixed(8). Comparing numerically with a tolerance keeps this working whatever
// the grid spacing is, instead of relying on both sides formatting identically.
const isPositionTaken = (
  point,
  taken,
  toleranceM = SAME_POSITION_TOLERANCE_M
) => {
  if (!point || !taken || !taken.length) {
    return false;
  }

  return taken.some((occupied) => {
    const distance = metresBetween(point, occupied);

    return isFinite(distance) && distance <= toleranceM;
  });
};

export const markerGridHelper = {
  boundsSizeInKm,
  cellSideForBounds,
  isPositionTaken,
  metresBetween,
  DEFAULT_CELL_SIDE_KM,
  MAX_GRID_POINTS,
  SAME_POSITION_TOLERANCE_M,
};
